import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { apiClient, formatApiError } from "@/lib/auth";
import { useAuth } from "@/lib/auth";
import FinanceLayout, { fmtUsd } from "./FinanceLayout";
import { Plus, CheckCircle2, XCircle, Loader2, ShieldCheck, Trash2, Pencil, Archive, ArchiveRestore, X } from "lucide-react";
import { toast } from "sonner";

const statusMeta = {
  pending_dual_approval: { label: "Pending Dual Approval", color: "text-amber-300", bg: "bg-amber-500/10 border-amber-500/30" },
  approved:              { label: "Approved",              color: "text-emerald-300", bg: "bg-emerald-500/10 border-emerald-500/30" },
  rejected:              { label: "Rejected",              color: "text-red-300",   bg: "bg-red-500/10 border-red-500/30" },
};

const CATEGORIES = [
  ["operating", "Operating"],
  ["cogs", "COGS"],
  ["reserve", "Reserve"],
];

const ExpensesPage = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [pending, setPending] = useState(0);
  const [threshold, setThreshold] = useState(1000);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState(searchParams.get("category") || "");
  const [showArchived, setShowArchived] = useState(false);
  // Add form
  const [amount, setAmount] = useState("");
  const [vendor, setVendor] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("operating");
  // Edit modal
  const [editing, setEditing] = useState(null);
  const [e_amount, setEAmount] = useState("");
  const [e_vendor, setEVendor] = useState("");
  const [e_desc, setEDesc] = useState("");
  const [e_category, setECategory] = useState("operating");
  const [e_date, setEDate] = useState("");

  const load = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      if (showArchived) params.set("include_archived", "true");
      const { data } = await apiClient.get(`/finance/expenses?${params.toString()}`);
      const filtered = categoryFilter
        ? data.items.filter((i) => (i.category || "").toLowerCase() === categoryFilter)
        : data.items;
      setItems(filtered);
      setPending(data.pending_dual_approval);
      setThreshold(data.threshold_usd);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, showArchived, categoryFilter]);

  // Keep URL param in sync with categoryFilter so back/forward works cleanly
  useEffect(() => {
    if (categoryFilter) {
      searchParams.set("category", categoryFilter);
    } else {
      searchParams.delete("category");
    }
    setSearchParams(searchParams, { replace: true });
    /* eslint-disable-next-line */
  }, [categoryFilter]);

  const submit = async (e) => {
    e.preventDefault();
    if (!amount || !vendor) return toast.error("Amount and vendor required");
    setBusy(true);
    try {
      const { data } = await apiClient.post("/finance/expenses", {
        amount: Number(amount), vendor, description, category,
      });
      const st = data.expense.status;
      toast.success(st === "pending_dual_approval"
        ? "Submitted — both partners have been notified"
        : "Expense recorded and approved");
      setAmount(""); setVendor(""); setDescription("");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const approve = async (id) => {
    try {
      const { data } = await apiClient.post(`/finance/expenses/${id}/approve`);
      toast.success(data.status === "approved" ? "Finalized — both partners signed off" : "Your approval logged. Waiting on the other partner.");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const reject = async (id) => {
    if (!window.confirm("Reject this expense?")) return;
    try {
      await apiClient.post(`/finance/expenses/${id}/reject`);
      toast.success("Rejected");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (exp) => {
    const partnerNote = user?.is_partner ? " (Partner: any expense, any status)" : "";
    if (!window.confirm(`Delete expense to ${exp.vendor} for ${fmtUsd(exp.amount)}?${partnerNote}`)) return;
    try {
      await apiClient.delete(`/finance/expenses/${exp.id}`);
      toast.success("Deleted");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openEdit = (exp) => {
    setEditing(exp);
    setEAmount(String(exp.amount));
    setEVendor(exp.vendor);
    setEDesc(exp.description || "");
    setECategory(exp.category || "operating");
    setEDate(exp.incurred_at || "");
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editing) return;
    setBusy(true);
    try {
      await apiClient.patch(`/finance/expenses/${editing.id}`, {
        amount: Number(e_amount),
        vendor: e_vendor,
        description: e_desc,
        category: e_category,
        incurred_at: e_date,
      });
      toast.success("Expense updated");
      setEditing(null);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const archive = async (exp) => {
    try {
      await apiClient.post(`/finance/expenses/${exp.id}/${exp.archived ? "unarchive" : "archive"}`);
      toast.success(exp.archived ? "Restored" : "Archived");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const canApprove = (exp) => {
    if (!user?.is_partner) return false;
    if (exp.status !== "pending_dual_approval") return false;
    const already = (exp.approvals || []).some((a) => a.partner_email === user.email);
    return !already;
  };

  const canDelete = (exp) => {
    if (user?.is_partner) return true;                                        // partners: full delete
    if (exp.submitted_by !== user?.email) return false;                        // staff: own only
    if (exp.status === "approved" || (exp.approvals || []).length > 0) return false;
    return true;
  };

  return (
    <FinanceLayout>
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <ShieldCheck size={20} className="text-nex-gold" />
          <div className="mono-label">// GOVERNANCE · DUAL APPROVAL</div>
        </div>
        <h1 className="serif-head text-3xl">Expenses</h1>
        <p className="text-nex-chrome mt-2 text-sm">
          Any expense over <span className="text-nex-gold">${threshold.toLocaleString()}</span> requires both partners to sign off before it&apos;s finalized.
          Currently <span className="text-amber-300">{pending} pending</span>.
          {user?.is_partner && <span className="ml-2 text-nex-gold mono-body text-[10px] uppercase tracking-widest">· Partner mode: edit / archive / delete any row</span>}
        </p>
      </div>

      {/* Submit form */}
      <form onSubmit={submit} className="card-industrial corner-tick p-5 mb-6 grid md:grid-cols-6 gap-3" data-testid="expense-form">
        <input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount" className="input-industrial" data-testid="exp-amount" />
        <input type="text" value={vendor} onChange={(e) => setVendor(e.target.value)} placeholder="Vendor" className="input-industrial" data-testid="exp-vendor" />
        <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description (optional)" className="input-industrial md:col-span-2" data-testid="exp-description" />
        <select value={category} onChange={(e) => setCategory(e.target.value)} className="input-industrial" data-testid="exp-category">
          {CATEGORIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <button type="submit" disabled={busy} className="tick-btn-primary justify-center" data-testid="exp-submit">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <><Plus size={13} /> Submit</>}
        </button>
      </form>

      <div className="mb-4 flex items-center gap-3 flex-wrap" data-testid="exp-filters">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="input-industrial md:w-64" data-testid="exp-status-filter">
          <option value="">All statuses</option>
          <option value="pending_dual_approval">Pending Dual Approval</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="input-industrial md:w-64" data-testid="exp-category-filter">
          <option value="">All categories</option>
          {CATEGORIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        {categoryFilter && (
          <button
            type="button"
            onClick={() => setCategoryFilter("")}
            data-testid="clear-category-filter"
            className="mono-body text-[10px] uppercase tracking-widest text-nex-gold hover:text-nex-silver inline-flex items-center gap-1"
          >
            <X size={11} /> Clear category
          </button>
        )}
        {user?.is_partner && (
          <label className="flex items-center gap-2 text-nex-chrome text-xs cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              data-testid="show-archived"
              className="accent-nex-gold"
            />
            Show archived
          </label>
        )}
      </div>

      <div className="card-industrial corner-tick overflow-hidden" data-testid="expenses-table">
        {loading ? (
          <div className="text-center py-16"><Loader2 className="animate-spin text-nex-gold mx-auto" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-nex-chrome text-sm">No expenses match this filter.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-black/30 border-b border-nex-border">
                <tr className="mono-body text-[10px] uppercase tracking-[0.15em] text-nex-chrome">
                  <th className="text-left px-4 py-3">Date</th>
                  <th className="text-left px-4 py-3">Vendor</th>
                  <th className="text-right px-4 py-3">Amount</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Approvals</th>
                  <th className="text-right px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((exp) => {
                  const meta = statusMeta[exp.status] || { label: exp.status, color: "text-nex-chrome", bg: "bg-white/5 border-white/10" };
                  return (
                    <tr key={exp.id} className={`border-b border-nex-border/50 hover:bg-white/[0.02] ${exp.archived ? "opacity-50" : ""}`} data-testid={`exp-row-${exp.id}`}>
                      <td className="px-4 py-3 mono-body text-[11px] text-nex-chrome">{exp.incurred_at}</td>
                      <td className="px-4 py-3">
                        <div className="text-nex-silver flex items-center gap-2">
                          {exp.vendor}
                          {exp.archived && <span className="mono-body text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-nex-border/40 text-nex-muted">Archived</span>}
                        </div>
                        {exp.description && <div className="text-nex-muted text-xs mt-1">{exp.description}</div>}
                        <div className="mono-body text-[10px] text-nex-muted mt-1">submitted by {exp.submitted_by}</div>
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-nex-silver">{fmtUsd(exp.amount)}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-1 border ${meta.bg} ${meta.color} mono-body text-[10px] tracking-[0.1em] uppercase`}>
                          {meta.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="mono-body text-[10px] text-nex-chrome space-y-1">
                          {(exp.approvals || []).map((a) => (
                            <div key={a.partner_email} className="text-emerald-300">✓ {a.partner_email.split("@")[0]}</div>
                          ))}
                          {(exp.approvals || []).length === 0 && <span className="text-nex-muted">—</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {canApprove(exp) && (
                          <>
                            <button onClick={() => approve(exp.id)} className="text-emerald-300 hover:text-emerald-200 mr-3" data-testid={`approve-${exp.id}`} title="Approve">
                              <CheckCircle2 size={16} />
                            </button>
                            <button onClick={() => reject(exp.id)} className="text-red-300 hover:text-red-200 mr-3" data-testid={`reject-${exp.id}`} title="Reject">
                              <XCircle size={16} />
                            </button>
                          </>
                        )}
                        {user?.is_partner && (
                          <>
                            <button onClick={() => openEdit(exp)} className="text-nex-chrome hover:text-nex-gold mr-3" data-testid={`edit-exp-${exp.id}`} title="Edit">
                              <Pencil size={14} />
                            </button>
                            <button onClick={() => archive(exp)} className="text-nex-chrome hover:text-nex-gold mr-3" data-testid={`archive-exp-${exp.id}`} title={exp.archived ? "Restore" : "Archive"}>
                              {exp.archived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
                            </button>
                          </>
                        )}
                        {canDelete(exp) && (
                          <button onClick={() => remove(exp)} className="text-nex-muted hover:text-red-300" data-testid={`delete-exp-${exp.id}`} title="Delete">
                            <Trash2 size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* EDIT MODAL */}
      {editing && (
        <>
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[75]" onClick={() => setEditing(null)} />
          <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[80] w-full max-w-lg px-4" data-testid="edit-expense-modal">
            <div className="card-industrial corner-tick p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="serif-head text-xl">Edit Expense</h3>
                <button onClick={() => setEditing(null)} className="text-nex-muted hover:text-nex-silver" data-testid="edit-close"><X size={18} /></button>
              </div>
              <form onSubmit={saveEdit} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <div className="mono-body text-[10px] uppercase tracking-widest text-nex-chrome mb-1">Amount ($)</div>
                    <input type="number" step="0.01" value={e_amount} onChange={(e) => setEAmount(e.target.value)} className="input-industrial w-full" data-testid="edit-amount" />
                  </label>
                  <label className="block">
                    <div className="mono-body text-[10px] uppercase tracking-widest text-nex-chrome mb-1">Incurred date</div>
                    <input type="date" value={e_date} onChange={(e) => setEDate(e.target.value)} className="input-industrial w-full" data-testid="edit-date" />
                  </label>
                </div>
                <label className="block">
                  <div className="mono-body text-[10px] uppercase tracking-widest text-nex-chrome mb-1">Vendor</div>
                  <input type="text" value={e_vendor} onChange={(e) => setEVendor(e.target.value)} className="input-industrial w-full" data-testid="edit-vendor" />
                </label>
                <label className="block">
                  <div className="mono-body text-[10px] uppercase tracking-widest text-nex-chrome mb-1">Description</div>
                  <input type="text" value={e_desc} onChange={(e) => setEDesc(e.target.value)} className="input-industrial w-full" data-testid="edit-desc" />
                </label>
                <label className="block">
                  <div className="mono-body text-[10px] uppercase tracking-widest text-nex-chrome mb-1">Category</div>
                  <select value={e_category} onChange={(e) => setECategory(e.target.value)} className="input-industrial w-full" data-testid="edit-category">
                    {CATEGORIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                </label>
                <button type="submit" disabled={busy} className="tick-btn-primary w-full justify-center mt-2" data-testid="edit-save">
                  {busy ? <Loader2 size={13} className="animate-spin" /> : "Save changes"}
                </button>
              </form>
            </div>
          </div>
        </>
      )}
    </FinanceLayout>
  );
};

export default ExpensesPage;
